import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { apiService } from '../services/api';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const BOX_CLASS = 'bg-accent-3 rounded-lg shadow-md border-2 border-transparent overflow-hidden';

const CHART_COLORS = [
  '#5a3e1b', '#8B5E34', '#1a5276', '#2E7D32', '#922b21', '#6c3483',
  '#0e6655', '#b9770e', '#1b4f72', '#c0392b',
];

function formatChartLabel(dateStr) {
  return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: '2-digit' });
}

const StatisticsPage = ({ onClose, embedded }) => {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const loadStats = async () => {
      try {
        const data = await apiService.getStatsOverview();
        if (data.success) {
          setStats(data);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Statistiken:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadStats();
  }, []);

  if (isLoading) {
    return (
      <div className="p-4 flex items-center justify-center min-h-[200px]">
        <p className="text-text-normal">Statistiken werden geladen...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-4">
        <p className="text-text-normal">Keine Statistiken verfügbar.</p>
        {!embedded && onClose && (
          <button type="button" onClick={onClose} className="mt-2 text-accent-1 underline">Schließen</button>
        )}
      </div>
    );
  }

  const tabs = [
    { id: 'my_stats', label: 'Meine Statistik', icon: '🙋' },
    { id: 'overview', label: 'Übersicht', icon: '📊' },
    { id: 'verlauf', label: 'Verlauf', icon: '📈' },
    { id: 'revenue', label: 'Umsatz & Mitglieder', icon: '💰' },
    { id: 'drinks', label: 'Getränke', icon: '🍺' },
    { id: 'attendance', label: 'Anwesenheit', icon: '👥' },
  ];

  const chartDates = stats.chart_dates || [];
  const drinksCountByDate = (stats.drinks_per_day || []).reduce((acc, r) => { acc[r.date] = r.count; return acc; }, {});
  const attendanceByDate = (stats.attendance_per_day || []).reduce((acc, r) => { acc[r.date] = r.member_count; return acc; }, {});
  const chartLabels = chartDates.map(formatChartLabel);

  const chart1Data = {
    labels: chartLabels,
    datasets: [
      {
        label: 'Getränke',
        data: chartDates.map((d) => drinksCountByDate[d] ?? 0),
        borderColor: '#5a3e1b',
        backgroundColor: 'rgba(90, 62, 27, 0.2)',
        fill: true,
        tension: 0.3,
      },
      {
        label: 'Anwesend (Personen)',
        data: chartDates.map((d) => attendanceByDate[d] ?? 0),
        borderColor: '#1a5276',
        backgroundColor: 'rgba(26, 82, 118, 0.2)',
        fill: true,
        tension: 0.3,
      },
    ],
  };

  const chart2Data = {
    labels: chartLabels,
    datasets: [
      {
        label: 'Gesamtkosten (€)',
        data: (stats.revenue_per_day || []).map((r) => r.total_amount),
        borderColor: '#1e5c23',
        backgroundColor: 'rgba(30, 92, 35, 0.25)',
        fill: true,
        tension: 0.3,
      },
    ],
  };

  const chart3Data = {
    labels: chartLabels,
    datasets: (stats.drinks_per_day_by_drink || []).map((drink, i) => ({
      label: drink.name,
      data: drink.counts,
      borderColor: CHART_COLORS[i % CHART_COLORS.length],
      backgroundColor: 'transparent',
      fill: false,
      tension: 0.3,
    })),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { color: '#2B1D14' } },
      tooltip: { titleColor: '#F6F0E8', bodyColor: '#E7DFD5' },
    },
    scales: {
      x: {
        grid: { color: 'rgba(43, 29, 20, 0.15)' },
        ticks: { color: '#2B1D14', maxRotation: 45 },
      },
      y: {
        grid: { color: 'rgba(43, 29, 20, 0.15)' },
        ticks: { color: '#2B1D14' },
        beginAtZero: true,
      },
    },
  };

  const totalDrinksYear = stats.drink_totals_year?.reduce((sum, r) => sum + r.count, 0) ?? 0;
  const totalEvenings = stats.attendance_per_day?.length ?? 0;

  return (
    <div className={embedded ? 'px-4 pt-0 pb-6' : 'p-4 pb-8'}>
      <div className={embedded ? 'mb-2' : 'mb-4'}>
        <h2 className="text-lg font-heading font-bold text-text-strong">Statistik</h2>
        {!embedded && onClose && (
          <button type="button" onClick={onClose} className="mt-2 text-accent-1 font-medium hover:underline">
            Schließen
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-shrink-0 px-4 py-2.5 rounded-lg font-medium transition-all duration-200 ${
              activeTab === tab.id
                ? 'bg-accent-1 text-white shadow-md'
                : 'bg-accent-3 text-text-strong border-2 border-transparent hover:border-accent-1/50'
            }`}
          >
            <span className="mr-1.5">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-6">
          <h3 className="text-base font-heading font-semibold text-text-strong">Highlights</h3>
          <div className="grid grid-cols-2 gap-3 w-full">
            <div className={BOX_CLASS + ' p-3'}>
              <p className="text-bg-1 text-sm font-medium">Getränke dieses Jahr</p>
              <p className="text-xl font-bold text-bg-1 mt-0.5">{totalDrinksYear}</p>
            </div>
            <div className={BOX_CLASS + ' p-3'}>
              <p className="text-bg-1 text-sm font-medium">Abende mit Aktivität</p>
              <p className="text-xl font-bold text-bg-1 mt-0.5">{totalEvenings}</p>
            </div>
          </div>
          <h3 className="text-base font-heading font-semibold text-text-strong">Top 3 Umsatz</h3>
          <div className={BOX_CLASS}>
            <table className="min-w-full text-sm">
              <thead className="bg-bg-3">
                <tr>
                  <th className="px-4 py-2 text-left text-text-subtle font-medium">Mitglied</th>
                  <th className="px-4 py-2 text-right text-text-subtle font-medium">Umsatz</th>
                </tr>
              </thead>
              <tbody className="text-bg-1">
                {(stats.top_members_by_revenue || []).slice(0, 3).map((row) => (
                  <tr key={row.member_id} className="border-t border-accent-1/20">
                    <td className="px-4 py-2">{row.username}</td>
                    <td className="px-4 py-2 text-right font-medium">{row.total_amount.toFixed(2)} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h3 className="text-base font-heading font-semibold text-text-strong">Top 3 Getränke</h3>
          <div className={BOX_CLASS}>
            <table className="min-w-full text-sm">
              <thead className="bg-bg-3">
                <tr>
                  <th className="px-4 py-2 text-left text-text-subtle font-medium">Mitglied</th>
                  <th className="px-4 py-2 text-right text-text-subtle font-medium">Getränke</th>
                </tr>
              </thead>
              <tbody className="text-bg-1">
                {(stats.top_members_by_drinks || []).slice(0, 3).map((row) => (
                  <tr key={row.member_id} className="border-t border-accent-1/20">
                    <td className="px-4 py-2">{row.username}</td>
                    <td className="px-4 py-2 text-right font-medium">{row.drink_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'my_stats' && (
        <div className="space-y-6">
          <h3 className="text-base font-heading font-semibold text-text-strong">Deine persönliche Übersicht</h3>
          {stats.my_stats ? (
            <>
              <div className="grid grid-cols-3 gap-3 w-full">
                <div className={BOX_CLASS + ' p-3'}>
                  <p className="text-bg-1 text-sm font-medium">Getrunkene Getränke</p>
                  <p className="text-xl font-bold text-bg-1 mt-0.5">{stats.my_stats.total_drinks}</p>
                </div>
                <div className={BOX_CLASS + ' p-3'}>
                  <p className="text-bg-1 text-sm font-medium">Bezahlt gesamt</p>
                  <p className="text-xl font-bold text-bg-1 mt-0.5">{(stats.my_stats.total_paid || 0).toFixed(2)} €</p>
                </div>
                <div className={BOX_CLASS + ' p-3'}>
                  <p className="text-bg-1 text-sm font-medium">Anwesenheiten</p>
                  <p className="text-xl font-bold text-bg-1 mt-0.5">{stats.my_stats.attendance_count}</p>
                </div>
              </div>
              <div className={BOX_CLASS}>
                <table className="min-w-full text-sm">
                  <thead className="bg-bg-3">
                    <tr>
                      <th className="px-4 py-2 text-left text-text-subtle font-medium">Getränk</th>
                      <th className="px-4 py-2 text-right text-text-subtle font-medium">Anzahl</th>
                    </tr>
                  </thead>
                  <tbody className="text-bg-1">
                    {(stats.my_stats.drinks_by_type || []).map((row) => (
                      <tr key={row.drink_id} className="border-t border-accent-1/20">
                        <td className="px-4 py-2">{row.name}</td>
                        <td className="px-4 py-2 text-right font-medium">{row.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <p className="text-text-subtle text-sm">Keine persönlichen Statistiken verfügbar.</p>
          )}
        </div>
      )}

      {activeTab === 'verlauf' && (
        <div className="space-y-8">
          <p className="text-text-subtle text-sm">Nur Tage mit Aktivität (z. B. Stammtisch-Abende).</p>
          <section>
            <h3 className="text-base font-heading font-bold text-text-strong mb-3">Getränke & Anwesenheit im Verlauf</h3>
            <div className={BOX_CLASS + ' p-4'} style={{ minHeight: 280 }}>
              {chartDates.length > 0 ? (
                <Line data={chart1Data} options={chartOptions} height={280} />
              ) : (
                <p className="text-text-subtle text-sm">Noch keine Daten für einen Verlauf vorhanden.</p>
              )}
            </div>
          </section>
          <section>
            <h3 className="text-base font-heading font-bold text-text-strong mb-3">Gesamtkosten des Stammtischs im Verlauf</h3>
            <div className={BOX_CLASS + ' p-4'} style={{ minHeight: 280 }}>
              {chartDates.length > 0 ? (
                <Line data={chart2Data} options={chartOptions} height={280} />
              ) : (
                <p className="text-text-subtle text-sm">Noch keine Daten für einen Verlauf vorhanden.</p>
              )}
            </div>
          </section>
          <section>
            <h3 className="text-base font-heading font-bold text-text-strong mb-3">Getränke pro Sorte im Verlauf</h3>
            <div className={BOX_CLASS + ' p-4'} style={{ minHeight: 280 }}>
              {chartDates.length > 0 && (stats.drinks_per_day_by_drink || []).length > 0 ? (
                <Line data={chart3Data} options={chartOptions} height={280} />
              ) : (
                <p className="text-text-subtle text-sm">Noch keine Daten für einen Verlauf vorhanden.</p>
              )}
            </div>
          </section>
        </div>
      )}

      {activeTab === 'revenue' && (
        <div className="space-y-6">
          <section>
            <h3 className="text-base font-heading font-bold text-text-strong mb-3">Top-Umsatz nach Mitglied</h3>
            <div className={BOX_CLASS}>
              <table className="min-w-full text-sm">
                <thead className="bg-bg-3">
                  <tr>
                    <th className="px-4 py-2 text-left text-text-subtle font-medium">Mitglied</th>
                    <th className="px-4 py-2 text-right text-text-subtle font-medium">Umsatz</th>
                  </tr>
                </thead>
                <tbody className="text-bg-1">
                  {(stats.top_members_by_revenue || []).map((row) => (
                    <tr key={row.member_id} className="border-t border-accent-1/20">
                      <td className="px-4 py-2">{row.username}</td>
                      <td className="px-4 py-2 text-right font-medium">{row.total_amount.toFixed(2)} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <section>
            <h3 className="text-base font-heading font-bold text-text-strong mb-3">Meiste Getränke nach Mitglied</h3>
            <div className={BOX_CLASS}>
              <table className="min-w-full text-sm">
                <thead className="bg-bg-3">
                  <tr>
                    <th className="px-4 py-2 text-left text-text-subtle font-medium">Mitglied</th>
                    <th className="px-4 py-2 text-right text-text-subtle font-medium">Getränke</th>
                  </tr>
                </thead>
                <tbody className="text-bg-1">
                  {(stats.top_members_by_drinks || []).map((row) => (
                    <tr key={row.member_id} className="border-t border-accent-1/20">
                      <td className="px-4 py-2">{row.username}</td>
                      <td className="px-4 py-2 text-right font-medium">{row.drink_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {activeTab === 'drinks' && (
        <div className="space-y-6">
          <h3 className="text-base font-heading font-bold text-text-strong">Verbrauch pro Getränk (dieses Jahr)</h3>
          <div className={BOX_CLASS}>
            <table className="min-w-full text-sm">
              <thead className="bg-bg-3">
                <tr>
                  <th className="px-4 py-2 text-left text-text-subtle font-medium">Getränk</th>
                  <th className="px-4 py-2 text-right text-text-subtle font-medium">Anzahl</th>
                </tr>
              </thead>
              <tbody className="text-bg-1">
                {(stats.drink_totals_year || []).map((row) => (
                  <tr key={row.drink_id} className="border-t border-accent-1/20">
                    <td className="px-4 py-2">{row.name}</td>
                    <td className="px-4 py-2 text-right font-medium">{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'attendance' && (
        <div className="space-y-6">
          <h3 className="text-base font-heading font-bold text-text-strong">Anwesenheit pro Abend</h3>
          <p className="text-text-subtle text-sm">Anzahl Mitglieder mit mind. einer Buchung pro Tag.</p>
          <div className={BOX_CLASS + ' max-h-[40vh] overflow-y-auto'}>
            <table className="min-w-full text-sm">
              <thead className="bg-bg-3 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-text-subtle font-medium">Datum</th>
                  <th className="px-4 py-2 text-right text-text-subtle font-medium">Personen</th>
                </tr>
              </thead>
              <tbody className="text-bg-1">
                {(stats.attendance_per_day || []).slice().reverse().map((row) => (
                  <tr key={row.date} className="border-t border-accent-1/20">
                    <td className="px-4 py-2">
                      {new Date(row.date).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </td>
                    <td className="px-4 py-2 text-right font-medium">{row.member_count} {row.member_count === 1 ? 'Person' : 'Personen'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h3 className="text-base font-heading font-semibold text-text-strong">Letzte Anwesenheit pro Mitglied</h3>
          <div className={BOX_CLASS}>
            <table className="min-w-full text-sm">
              <thead className="bg-bg-3">
                <tr>
                  <th className="px-4 py-2 text-left text-text-subtle font-medium">Mitglied</th>
                  <th className="px-4 py-2 text-left text-text-subtle font-medium">Letzte Anwesenheit</th>
                  <th className="px-4 py-2 text-right text-text-subtle font-medium">Vor</th>
                </tr>
              </thead>
              <tbody className="text-bg-1">
                {(stats.members_last_attendance || []).map((row) => {
                  const lastDate = row.last_active_at ? new Date(row.last_active_at) : null;
                  const weeksAgo = lastDate
                    ? Math.floor((Date.now() - lastDate.getTime()) / (7 * 24 * 60 * 60 * 1000))
                    : null;
                  return (
                    <tr key={row.member_id} className="border-t border-accent-1/20">
                      <td className="px-4 py-2">{row.username}</td>
                      <td className="px-4 py-2">
                        {lastDate
                          ? lastDate.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })
                          : '–'}
                      </td>
                      <td className="px-4 py-2 text-right font-medium">
                        {weeksAgo !== null
                          ? weeksAgo === 0
                            ? 'Diese Woche'
                            : weeksAgo === 1
                              ? '1 Woche'
                              : `${weeksAgo} Wochen`
                          : '–'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <section>
              <h3 className="text-base font-heading font-semibold text-text-strong mb-3">Top 3 Anwesenheit</h3>
              <div className={BOX_CLASS}>
                <table className="min-w-full text-sm">
                  <thead className="bg-bg-3">
                    <tr>
                      <th className="px-4 py-2 text-left text-text-subtle font-medium">Mitglied</th>
                      <th className="px-4 py-2 text-right text-text-subtle font-medium">Abende</th>
                    </tr>
                  </thead>
                  <tbody className="text-bg-1">
                    {(stats.top_attendance_members || []).map((row) => (
                      <tr key={row.member_id} className="border-t border-accent-1/20">
                        <td className="px-4 py-2">{row.username}</td>
                        <td className="px-4 py-2 text-right font-medium">{row.attendance_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
            <section>
              <h3 className="text-base font-heading font-semibold text-text-strong mb-3">Top 3 selten anwesend</h3>
              <div className={BOX_CLASS}>
                <table className="min-w-full text-sm">
                  <thead className="bg-bg-3">
                    <tr>
                      <th className="px-4 py-2 text-left text-text-subtle font-medium">Mitglied</th>
                      <th className="px-4 py-2 text-right text-text-subtle font-medium">Abende</th>
                    </tr>
                  </thead>
                  <tbody className="text-bg-1">
                    {(stats.least_attendance_members || []).map((row) => (
                      <tr key={row.member_id} className="border-t border-accent-1/20">
                        <td className="px-4 py-2">{row.username}</td>
                        <td className="px-4 py-2 text-right font-medium">{row.attendance_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatisticsPage;
